/** The asset rail's tabs and the rows each tab lists, in display order. */
import type { EquipmentTypeLabel } from '../hooks/useEquipmentTypes';
import type { MarkingKind, PnidStateResponse, PnidWizardState } from '../types';
import { agentReviewKey } from './agentReviewItems';
import {
  equipmentTargetGroups,
  instrumentTargetGroups,
  nodeTargets,
  type TargetRailGroup,
  type TargetRailItem,
} from './targetRailItems';
import { matchesRailQuery } from './WorkspaceTargetRail';

export type AssetRailTab = 'equipment' | 'instruments' | 'lines';

export const TABS: { value: AssetRailTab; label: string }[] = [
  { value: 'equipment', label: 'Equipments' },
  { value: 'instruments', label: 'Instruments' },
  { value: 'lines', label: 'Lines' },
];

/** The drawing as the rail reads it: the three kinds it lists, and the findings that mark rows. */
export type RailAssets = Pick<PnidWizardState, 'equipments' | 'instruments' | 'nodes'>
  & Pick<PnidStateResponse, 'validation'>;

/** The tab is the filter's kind tier. */
export const TAB_KINDS: Record<AssetRailTab, MarkingKind> = { equipment: 'equipment', instruments: 'instrument', lines: 'node' };

/** One tab's rows, and for Equipment and Instruments the type groups they render under.
 *  With `publishableInstrumentIds`, the Instruments tab lists only those instruments; without it,
 *  it lists them all. */
export function assetRailTabRows(
  tab: AssetRailTab,
  assets: RailAssets | undefined,
  equipmentTypeLabel: EquipmentTypeLabel,
  publishableInstrumentIds?: ReadonlySet<string>,
): { items: TargetRailItem[]; groups: TargetRailGroup[] | undefined } {
  if (!assets) return { items: [], groups: tab === 'lines' ? undefined : [] };
  const { instruments, validation } = assets;
  const listedInstruments = publishableInstrumentIds
    ? instruments.filter((instrument) => publishableInstrumentIds.has(instrument.id))
    : instruments;
  const groups = tab === 'instruments' ? instrumentTargetGroups(listedInstruments, validation)
    : tab === 'equipment' ? equipmentTargetGroups(assets.equipments, equipmentTypeLabel, validation)
    : undefined;
  const items = groups ? groups.flatMap((group) => group.items) : nodeTargets(assets.nodes, validation);
  return { items, groups };
}

/**
 * The rows the Needs review advance walks, as `kind:id` keys: the pending rows `fromTab` shows, then
 * the pending rows of each later tab in tab order, each tab in its own row order. A later tab opens
 * with Type and Subtype clear, so all its pending rows count. The search carries over a tab change,
 * so it narrows every tab.
 */
export function flaggedRowOrder({
  fromTab, shownIds, assets, equipmentTypeLabel, query, pendingIds, publishableInstrumentIds,
}: {
  fromTab: AssetRailTab;
  /** The rows `fromTab` shows after its Type and Subtype. Omitted, the tab's rows all count. */
  shownIds?: readonly string[];
  assets: RailAssets | undefined;
  /** Orders the Equipment tab's groups as the rail does. */
  equipmentTypeLabel: EquipmentTypeLabel;
  query: string;
  pendingIds: Record<MarkingKind, ReadonlySet<string>>;
  /** The Instruments tab's rows, as the rail lists them. */
  publishableInstrumentIds?: ReadonlySet<string>;
}): string[] {
  const tabs = TABS.slice(TABS.findIndex((candidate) => candidate.value === fromTab));
  return tabs.flatMap(({ value }) => {
    const kind = TAB_KINDS[value];
    return assetRailTabRows(value, assets, equipmentTypeLabel, publishableInstrumentIds).items
      .filter((row) => pendingIds[kind].has(row.id) && matchesRailQuery(row, query)
        && (value !== fromTab || !shownIds || shownIds.includes(row.id)))
      .map((row) => agentReviewKey(kind, row.id));
  });
}
